import { useEffect, useRef } from 'react';

/** Route-local staging. Never captures input or changes the document's scroll position. */
export function useScrollScore(revision?: unknown) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = root.current;
    if (!node || typeof window.matchMedia !== 'function') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compact = window.matchMedia('(max-width: 700px)');
    if (!reduced || !compact) return;
    const acts = Array.from(node.querySelectorAll<HTMLElement>('[data-sc-device]'));
    let frame = 0;
    let disposed = false;
    const update = () => {
      frame = 0;
      if (disposed) return;
      node.dataset.motion = reduced.matches ? 'quiet' : 'on';
      const height = window.innerHeight;
      // Read every rect before writing styles. No idle animation loop.
      const samples = acts.map((act) => ({ act, rect: act.getBoundingClientRect() }));
      for (const { act, rect } of samples) {
        const progress = Math.max(0, Math.min(1, (height - rect.top) / (height + rect.height)));
        const entrance = Math.max(0, Math.min(1, (height * 0.94 - rect.top) / (height * 0.48)));
        act.style.setProperty('--sc-p', reduced.matches ? '0.5' : progress.toFixed(4));
        act.style.setProperty('--sc-enter', reduced.matches ? '1' : entrance.toFixed(4));
        act.style.setProperty('--sc-distance', compact.matches ? '24px' : '96px');
      }
    };
    const schedule = () => {
      if (!frame && !disposed) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    reduced.addEventListener('change', schedule);
    compact.addEventListener('change', schedule);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    acts.forEach((act) => observer?.observe(act));
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      reduced.removeEventListener('change', schedule);
      compact.removeEventListener('change', schedule);
      observer?.disconnect();
      delete node.dataset.motion;
    };
  }, [revision]);
  return root;
}
